import { observer } from 'mobx-react-lite'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useStore } from '~/app/store'
import { Button, PageTitlePrefixSlot, SVGEye } from '~/shared/ui'
import { ShippingOrderComments } from '~/entities/delivery/ui/ShippingOrderComments'
import { SVGPrint } from '~/shared/ui/SVG/SVGPrint'
import { SVGDone } from '~/shared/ui/SVG/SVGDone'
import { DeliveryTabs } from '~/widgets/delivery/tabs-delivery'
import { LEGAL_ENTITY_GREY_SHOP, useToast } from '~/shared/lib'
import Spinner from '~/shared/ui/spinner'
import { Skeleton } from '~/shared/ui/table/components/skeleton'
import { useErrorHandler } from '~/shared/lib/hooks/use-error-handler'
import {
  canStartAutoPrint,
  createOrderRequestGuard,
  runOrderScopedRequest,
} from '../lib/order-request-guard'
import { useShippingOrderLoader } from './lib/useShippingOrderLoader'
import { useCapsLockState } from '../model/caps-lock'
import { OrderDetailsHeader } from './OrderDetailsHeader'

const AUTO_PRINT_SHIPPING_METHODS = ['wildberries', 'ozon', 'yandex-market']

const isCapsLockEnabled = (event: KeyboardEvent) => {
  try {
    return event.getModifierState('CapsLock')
  } catch {
    return false
  }
}

const isEditableElement = (target: EventTarget | null) =>
  target instanceof HTMLInputElement ||
  target instanceof HTMLTextAreaElement ||
  target instanceof HTMLSelectElement

enum AutoPrintStatus {
  Idle = 'idle',
  Pending = 'pending',
  Success = 'success',
  Error = 'error',
}

export const ShippingOrderById = observer(() => {
  const { deliveryStore, authStore } = useStore()
  const {
    getShippingOrderById,
    shippingOrderById,
    loaders,
    printBlanc,
    printBlankInfo,
  } = deliveryStore

  const { legalEntity } = authStore
  const { t } = useTranslation(['common', 'delivery'])
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const toast = useToast()
  const { handleErrorWithToast } = useErrorHandler()
  const [autoPrintStatus, setAutoPrintStatus] = useState(AutoPrintStatus.Idle)
  const currentOrderIdRef = useRef(id)
  const autoPrintRequestGuardRef = useRef(createOrderRequestGuard())
  autoPrintRequestGuardRef.current.selectOrder(id)
  currentOrderIdRef.current = id
  const capsLockEnabled = useCapsLockState()
  const [hasConfirmedKitBarcodeRelabel, setHasConfirmedKitBarcodeRelabel] =
    useState(false)
  const [scannedComponentProgress, setScannedComponentProgress] = useState<{
    unitKey: string
    variationIds: string[]
  }>({ unitKey: '', variationIds: [] })
  const [stickerPrintFailedUnitKey, setStickerPrintFailedUnitKey] = useState<
    string | null
  >(null)
  const { isOrderLoading } = useShippingOrderLoader({
    id,
    getShippingOrderById,
  })
  const handleKeyboardEvent = useCallback((event: KeyboardEvent) => {
    if (
      isCapsLockEnabled(event) &&
      event.key !== 'CapsLock' &&
      isEditableElement(event.target)
    ) {
      event.preventDefault()
      event.stopImmediatePropagation()
    }
  }, [])
  const orderDetails = shippingOrderById?.orderDetails
  const kitBarcodeRelabelRequired =
    shippingOrderById?.detail?.kitBarcodeRelabelRequired === true
  const kitRelabel = shippingOrderById?.detail?.kitRelabel
  const kitRelabelRequired = kitRelabel?.required === true
  const legacyKitBarcodeRelabelRequired =
    kitBarcodeRelabelRequired && !kitRelabelRequired
  const activeKitUnit = kitRelabelRequired
    ? (kitRelabel?.units.find((unit) => !unit.confirmed) ?? null)
    : null
  const activeKitUnitKey = activeKitUnit
    ? `${activeKitUnit.kitVariationId}:${activeKitUnit.unitIndex}`
    : ''
  const scannedComponentVariationIds =
    scannedComponentProgress.unitKey === activeKitUnitKey
      ? scannedComponentProgress.variationIds
      : []
  const stickerPrintFailed = stickerPrintFailedUnitKey === activeKitUnitKey
  const kitComponentsScanned =
    !!activeKitUnit &&
    scannedComponentVariationIds.length ===
      activeKitUnit.componentVariationIds.length
  const canScanLastItem = kitRelabelRequired
    ? kitRelabel?.pending === 0
    : !legacyKitBarcodeRelabelRequired || hasConfirmedKitBarcodeRelabel
  const comments = shippingOrderById?.detail?.comments ?? []
  const shippingMethod = orderDetails?.shippingMethod
  const totalItems = deliveryStore.itemsOrderById.length
  const processedItems = deliveryStore.itemsOrderById.filter(
    (item) => item.checked,
  ).length
  const allItemsScanned =
    !isOrderLoading &&
    deliveryStore.itemsOrderById.length > 0 &&
    deliveryStore.itemsOrderById.every((item) => item.checked)
  const showProcessingProgress = !isOrderLoading && totalItems > 10

  const shouldAutoPrint =
    !!shippingMethod && AUTO_PRINT_SHIPPING_METHODS.includes(shippingMethod)
  const isAutoPrinting = autoPrintStatus === AutoPrintStatus.Pending
  const isCheckingDisabled =
    isOrderLoading ||
    !allItemsScanned ||
    !canScanLastItem ||
    loaders.mandatoryMark ||
    (shouldAutoPrint && isAutoPrinting)

  useEffect(() => {
    setAutoPrintStatus(AutoPrintStatus.Idle)
    setHasConfirmedKitBarcodeRelabel(false)
    setScannedComponentProgress({ unitKey: '', variationIds: [] })
    setStickerPrintFailedUnitKey(null)
  }, [id])

  useEffect(
    () => () => {
      autoPrintRequestGuardRef.current.invalidate()
    },
    [],
  )

  useLayoutEffect(() => {
    window.addEventListener('keydown', handleKeyboardEvent, true)

    return () => {
      window.removeEventListener('keydown', handleKeyboardEvent, true)
    }
  }, [handleKeyboardEvent])

  const handleAutoPrint = async () => {
    if (
      !id ||
      isOrderLoading ||
      !printBlankInfo ||
      !shouldAutoPrint ||
      !canStartAutoPrint(autoPrintStatus)
    ) {
      return
    }

    const requestOrderId = id
    const requestLease = autoPrintRequestGuardRef.current.begin(requestOrderId)
    setAutoPrintStatus(AutoPrintStatus.Pending)

    await runOrderScopedRequest({
      guard: autoPrintRequestGuardRef.current,
      lease: requestLease,
      getCurrentOrderId: () => currentOrderIdRef.current,
      request: () => printBlanc(printBlankInfo, requestOrderId, 'printBlank'),
      onSuccess: (response) => {
        setAutoPrintStatus(AutoPrintStatus.Success)
        toast.open(response, 'success')
      },
      onError: (error) => {
        handleErrorWithToast(error)
        setAutoPrintStatus(AutoPrintStatus.Error)
      },
    })
  }

  const handleKitComponentScanned = (productVariationId: string) => {
    if (!activeKitUnit || activeKitUnit.stickerPrinted) return false

    if (
      !activeKitUnit.componentVariationIds.includes(productVariationId) ||
      scannedComponentVariationIds.includes(productVariationId)
    ) {
      toast.open(t('delivery:kitComponentNotExpected'), 'danger')
      return false
    }

    if (
      !deliveryStore.markKitComponentChecked(
        activeKitUnit.kitVariationId,
        productVariationId,
      )
    ) {
      toast.open(t('delivery:kitComponentPositionUnavailable'), 'danger')
      return false
    }

    setScannedComponentProgress((progress) => ({
      unitKey: activeKitUnitKey,
      variationIds: [
        ...(progress.unitKey === activeKitUnitKey ? progress.variationIds : []),
        productVariationId,
      ],
    }))
    return true
  }

  const handlePrintKitSticker = async () => {
    if (!id || !activeKitUnit || !kitComponentsScanned) return

    try {
      const result = await deliveryStore.printKitRelabelSticker(id, {
        kitVariationId: activeKitUnit.kitVariationId,
        unitIndex: activeKitUnit.unitIndex,
      })
      if (deliveryStore.currentOrderId === id && result.printed) {
        setStickerPrintFailedUnitKey(null)
        toast.open(t('delivery:kitStickerPrinted'), 'success')
      }
    } catch (error) {
      setStickerPrintFailedUnitKey(activeKitUnitKey)
      handleErrorWithToast(error)
    }
  }

  const handleKitNewSkuScanned = async (barcode: string) => {
    if (!id || !activeKitUnit?.stickerPrinted) return false

    try {
      const state = await deliveryStore.confirmKitRelabel(id, {
        kitVariationId: activeKitUnit.kitVariationId,
        unitIndex: activeKitUnit.unitIndex,
        barcode,
      })
      if (deliveryStore.currentOrderId !== id) return false

      toast.open(t('delivery:kitRelabelConfirmed'), 'success')
      if (state.pending === 0 && allItemsScanned) {
        await handleAutoPrint()
      }
      return true
    } catch (error) {
      handleErrorWithToast(error)
      return false
    }
  }

  let kitRelabelStep = <p>{t('delivery:kitRelabelComplete')}</p>
  if (activeKitUnit?.stickerPrinted) {
    kitRelabelStep = <p>{t('delivery:scanNewKitBarcode')}</p>
  } else if (activeKitUnit) {
    kitRelabelStep = (
      <>
        <p>
          {t('delivery:scanKitComponents', {
            unitIndex: activeKitUnit.unitIndex,
            scanned: scannedComponentVariationIds.length,
            total: activeKitUnit.componentVariationIds.length,
          })}
        </p>
        <Button
          intent="primary"
          disabled={!kitComponentsScanned || loaders.shippingOrderById}
          onClick={handlePrintKitSticker}
        >
          {t(
            stickerPrintFailed
              ? 'delivery:retryPrintKitSticker'
              : 'delivery:printKitSticker',
          )}
        </Button>
      </>
    )
  }

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 pt-6 lg:grid-cols-[minmax(0,1fr)_auto_auto]">
      <div className="col-span-full min-w-0 lg:col-span-1">
        {isOrderLoading ? (
          <div className="w-[300px] h-[150px]">
            <Skeleton />
          </div>
        ) : (
          <OrderDetailsHeader
            orderDetails={orderDetails}
            shippingMethod={shippingMethod}
            titlePrefixSlot={<PageTitlePrefixSlot />}
          />
        )}
      </div>
      {kitRelabelRequired && (
        <div
          className="col-span-full flex flex-col gap-3 rounded-lg border border-blue-300 bg-blue-50 p-5 text-blue-950"
          role="status"
        >
          <p className="text-lg font-semibold">
            {t('delivery:kitRelabelProgress', {
              pending: kitRelabel?.pending ?? 0,
            })}
          </p>
          {kitRelabelStep}
        </div>
      )}
      {legacyKitBarcodeRelabelRequired && !hasConfirmedKitBarcodeRelabel && (
        <div
          className="col-span-full flex flex-col gap-4 rounded-lg border-4 border-red-700 bg-red-50 p-6 text-red-950 shadow-lg"
          role="alert"
        >
          <p className="text-2xl font-bold">
            {t('delivery:kitBarcodeRelabelWarning')}
          </p>
          <Button
            intent="danger"
            onClick={() => setHasConfirmedKitBarcodeRelabel(true)}
          >
            {t('delivery:kitBarcodeRelabelConfirm')}
          </Button>
        </div>
      )}
      <div className="flex h-[48px] items-center justify-end gap-3">
        <Link to={`/orders/${id}`}>
          <Button>
            <SVGEye icon="visibility" color="white" />
            {t('delivery:viewOrder')}
          </Button>
        </Link>
        <Button
          disabled={loaders.printBlanc || !canScanLastItem}
          onClick={async () => {
            const requestOrderId = id
            try {
              if (printBlankInfo) {
                const action =
                  legalEntity === LEGAL_ENTITY_GREY_SHOP
                    ? 'printBlank'
                    : 'downloadBlank'

                const response = await printBlanc(
                  printBlankInfo,
                  requestOrderId as string,
                  action,
                )
                if (currentOrderIdRef.current !== requestOrderId) return

                if (
                  legalEntity !== LEGAL_ENTITY_GREY_SHOP &&
                  response?.attachment.url
                ) {
                  window.open(response.attachment.url, '_blank')
                }

                if (action === 'printBlank') {
                  if (shouldAutoPrint && allItemsScanned) {
                    setAutoPrintStatus(AutoPrintStatus.Success)
                  }

                  toast.open(response, 'success')
                }
              }
            } catch (error) {
              if (currentOrderIdRef.current !== requestOrderId) return
              handleErrorWithToast(error)
            }
          }}
        >
          {loaders.printBlanc ? <Spinner /> : <SVGPrint color="white" />}
          {t('delivery:blank')}
        </Button>
      </div>
      <div
        className={`justify-self-end self-center ${
          showProcessingProgress ? 'sticky top-[72px] z-40 lg:top-4' : ''
        }`}
      >
        <Button
          intent="success"
          disabled={isCheckingDisabled}
          onClick={async () => {
            try {
              await deliveryStore.checkingItemsOrderById(id as string)
              toast.open(t('delivery:orderSentSuccess'), 'success')
              navigate('/shipping/orders')
            } catch (error) {
              handleErrorWithToast(error)
            }
          }}
        >
          {isOrderLoading || loaders.mandatoryMark || isAutoPrinting ? (
            <Spinner />
          ) : (
            <>
              <SVGDone color="white" size="24" />
              {showProcessingProgress && !allItemsScanned
                ? t('delivery:processed', {
                    processed: processedItems,
                    total: totalItems,
                  })
                : t('delivery:check')}
            </>
          )}
        </Button>
      </div>
      <div className="col-span-full">
        {capsLockEnabled === true && (
          <div
            className="mb-4 rounded-lg border border-amber-500 bg-amber-50 px-4 py-3 text-amber-950"
            role="alert"
            aria-live="assertive"
          >
            {t('delivery:capsLockWarning')}
          </div>
        )}
        {isOrderLoading ? (
          <Spinner />
        ) : (
          <ShippingOrderComments comments={comments} />
        )}

        <DeliveryTabs
          canScanLastItem={canScanLastItem}
          onLastItemChecked={handleAutoPrint}
          capsLockEnabled={capsLockEnabled === true}
          kitRelabelRequired={kitRelabelRequired}
          kitRelabelUnit={activeKitUnit}
          onKitComponentScanned={handleKitComponentScanned}
          onKitNewSkuScanned={handleKitNewSkuScanned}
        />
      </div>
    </div>
  )
})
